import useAuth from '@app/auth/use-auth';
import useGeneratePdf from '@app/components/reports/hooks/use-generate-pdf';
import {
  useGetSecrConfig,
  useGetSecrResults,
} from '@shared/api/hooks/ccf/secr';
import Text from '@shared/components/content/text';
import DownloadLoader from '@shared/components/download-loader';
import Logo from '@shared/components/logo/logo';
import { cn, formatDate } from '@shared/lib/utils';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import { useCCF } from '../ccf-context';
import { SECRReportCombinedTable } from './components/secr-report-combined-table';
import { SECRReportIntensityTable } from './components/secr-report-intensity-table';
import { SECRReportResultsTable } from './components/secr-report-results-table';
import {
  buildMethodologyText,
  deriveYearLabels,
  formatPeriod,
} from './secr-report-utils';

const PAGE_VARS = '[--page-width:21cm] [--page-height:29.7cm] w-[--page-width]';
const PAGE = 'w-[--page-width] h-[--page-height] overflow-hidden';

function CoverPage({
  organisationName,
  deliveredOn,
}: {
  organisationName: string;
  deliveredOn: string;
}) {
  return (
    <div className={cn(PAGE, 'bg-primary flex flex-col justify-between p-16')}>
      <Logo variant="name-white-png" className="w-56" />
      <div className="space-y-6">
        <Text
          variant="h1"
          className="font-heading text-5xl uppercase text-gray-00"
        >
          {organisationName} SECR Report
        </Text>
        <Text className="text-lg text-gray-00/80">
          Delivered on: {deliveredOn}
        </Text>
      </div>
      <Text className="text-sm text-gray-00/60">
        ©{new Date().getFullYear()} My Emissions Ltd
      </Text>
    </div>
  );
}

function ContentPage({ children }: { children: React.ReactNode }) {
  return (
    <div className={cn(PAGE, 'flex flex-col bg-gray-00')}>
      <div className="bg-primary flex items-center px-12 py-4">
        <Logo variant="name-white-png" className="w-40" />
      </div>
      <div className="flex-1 space-y-8 px-12 py-8">{children}</div>
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <Text variant="h2" className="font-heading text-primary pb-2 text-xl">
      {children}
    </Text>
  );
}

export default function SecrReportDownload() {
  const { uuid: reportUuid, startDate, endDate } = useCCF();
  const { user } = useAuth();
  const { data: result, status: resultStatus } = useGetSecrResults(reportUuid);
  const { data: config, status: configStatus } = useGetSecrConfig(reportUuid);

  const organisationName = user?.organizationName ?? 'Organisation';
  const ready =
    resultStatus === 'success' &&
    configStatus === 'success' &&
    result &&
    config;

  const title = ready
    ? `${organisationName} SECR Report ${deriveYearLabels(startDate).reportYear}`
    : undefined;

  const { contentRef, status: downloadStatus } = useGeneratePdf(title, 'p');

  const labels = {
    idle: 'Preparing SECR report...',
    generating: 'Generating PDF report...',
    generated: 'PDF report generated. You can now close this tab.',
  };

  if (!ready) {
    return <DownloadLoader labels={labels} />;
  }

  const hasPriorYear = result.totals.priorYearTonnes != null;
  const { prevYearLabel, currentYearLabel } = deriveYearLabels(startDate);
  const period = formatPeriod(startDate, endDate);
  const deliveredOn = format(new Date(), 'do MMMM yyyy');

  const methodologyText =
    config.methodology?.trim() ||
    buildMethodologyText({
      organisationName,
      period,
      hasPriorYear,
      intensityLabel: result.intensity?.label,
      intensityUnit: result.intensity?.unit,
    });
  const methodologyParagraphs = methodologyText.split('\n\n');
  const hasActions = !!config.actionsTaken?.trim();

  return (
    <div className="relative">
      <DownloadLoader downloadStatus={downloadStatus} labels={labels} />
      <main ref={contentRef} className={PAGE_VARS}>
        <CoverPage
          organisationName={organisationName}
          deliveredOn={deliveredOn}
        />

        <ContentPage>
          <Text className="text-sm">
            This report is prepared by My Emissions for {organisationName}
            &apos;s SECR submission for the period{' '}
            {formatDate(startDate, 'do MMMM yyyy')} –{' '}
            {formatDate(endDate, 'do MMMM yyyy')}.
          </Text>

          <section className="space-y-3">
            <SectionHeading>Methodology</SectionHeading>
            <div className="space-y-3 text-sm leading-relaxed text-foreground/80">
              {methodologyParagraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </section>
        </ContentPage>

        <ContentPage>
          {hasPriorYear ? (
            <>
              <section className="space-y-3">
                <SectionHeading>Energy results</SectionHeading>
                <SECRReportResultsTable
                  metric="energy"
                  categories={result.categories}
                  totals={result.totals}
                  hasPriorYear
                  prevYearLabel={prevYearLabel}
                  currentYearLabel={currentYearLabel}
                />
              </section>
              <section className="space-y-3">
                <SectionHeading>Emissions results</SectionHeading>
                <SECRReportResultsTable
                  metric="emissions"
                  categories={result.categories}
                  totals={result.totals}
                  hasPriorYear
                  prevYearLabel={prevYearLabel}
                  currentYearLabel={currentYearLabel}
                />
              </section>
            </>
          ) : (
            <section className="space-y-3">
              <SectionHeading>Energy and emissions data</SectionHeading>
              <SECRReportCombinedTable
                categories={result.categories}
                totals={result.totals}
                currentYearLabel={currentYearLabel}
              />
            </section>
          )}
        </ContentPage>

        <ContentPage>
          <section className="space-y-3">
            <SectionHeading>Intensity metric</SectionHeading>
            <SECRReportIntensityTable
              intensity={result.intensity}
              prevYearLabel={prevYearLabel}
              currentYearLabel={currentYearLabel}
            />
          </section>
          {hasActions && (
            <section className="space-y-3">
              <SectionHeading>Energy efficiency actions taken</SectionHeading>
              <div className="prose prose-sm text-foreground/80 max-w-none text-sm leading-relaxed">
                <ReactMarkdown>{config.actionsTaken!}</ReactMarkdown>
              </div>
            </section>
          )}
        </ContentPage>
      </main>
    </div>
  );
}
